import { Injectable } from '@angular/core';
import { StoreName } from '../entities/StoreName';
import {  Subject } from 'rxjs';
import * as dayjs from 'dayjs';
import * as isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import * as isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { LaborCategory } from '../entities/LaborCategory';
import { FieldActivity } from '../entities/FieldActivity';
import { Project } from '../entities/Project';
import { DbTransaction } from '../models/db/db-transaction';
import { GlobalConstants } from '../constant/constant';
import { RecordNotFoundException, RecordSoftDeletedException, StateValidationException } from '../utils/exceptions';
import { UserProject } from '../entities/UserProject';
import { DataChangeEmitPayload, DataChangeEmitWhat, DataChangeEmitWho } from '../models/data-change-emit/data-change-emit-payload';
import { Check, EntityHelper } from '../utils/entity-helper';
import { Callback, CallbackNames } from '../models/db/db-transaction-callback';
import { DataService } from './data.service';
import { UserProjectService } from './user-project.service';
import { UserFieldActivityService } from './user-field-activity.service';
import { DateTimeHelper, DateTimeType } from '../utils/date-time-helper';
import { LaborCategoryService } from './labor-category.service';
import { FieldActivityService } from './field-activity.service';
import { TaskService } from './task.service';
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

@Injectable({
  providedIn: 'root',
})
export class ProjectService extends DataService {
  private emitChangeSource = new Subject<DataChangeEmitPayload<Project>>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  constructor(
    private user2ProjectService: UserProjectService,
    private user2FieldActivityService: UserFieldActivityService,
    private taskService: TaskService,
    private laborCategoryService: LaborCategoryService,
    private fieldActivityService: FieldActivityService,
  ) {
    super();
  }

  // #region Utility

  emitChange(project: Project, who: DataChangeEmitWho, what: DataChangeEmitWhat) {
    this.emitChangeSource.next({ data: project, who: who, what: what });
  }

  getEmitChangeCallback(project: Project, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitChangeForProject + '_' + project.RecordId,
      fn: this.emitChange.bind(this, project, who, what),
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return (
      existingTransaction ||
      (await this.repository.startTransaction(
        [
          StoreName.Project,
          StoreName.Task,
          StoreName.LaborCategory,
          StoreName.User,
          StoreName.Users2FieldActivity,
          StoreName.Users2Project,
          StoreName.TimeManager2Project,
          StoreName.NotableActivityOption,
          StoreName.FieldActivity,
        ],
        'readwrite'
      ))
    );
  }

  // #endregion

  // #region Low-level Get One

  async getProjectByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Project> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId<Project>(StoreName.Project, onlineId, Project, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as Project;

    if (this.checkIsProjectSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  async getProjectByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Project> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<Project>(StoreName.Project, recordId, Project, transaction);

    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as Project;

    if (this.checkIsProjectSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  // #endregion
  // #region Low-level Get Many

  async getProjectsByOnlineIds(onlineIds: number[], existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Project[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<Project>(StoreName.Project, GlobalConstants.KEY_NAMES.Id, onlineIds, Project, transaction);
    let records = result as Project[];

    if (!includeDeleted) {
      records = result.filter(project => !this.checkIsProjectSoftDeleted(project));
    }

    return records;
  }

  // #endregion
  // #region Low-level Create

  private async createProject(who: DataChangeEmitWho, recordToCreate: Project, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LC1. Validate action
    await this.validateProjectCreation(transaction);

    // TODO: Impelment LC2 and LC3

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.Project, transaction);

    // LC5. Get created record
    const createdRecord = await this.getProjectByRecordId(recordId, transaction, includeDeleted);

    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // #region Low-level Update

  async updateProject(who: DataChangeEmitWho, recordToUpdate: Project, skipLocalCopyValidation: boolean, localCopy: Project | null, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LU1. Get existing record
    const existingRecord = await this.getProjectByRecordId(recordToUpdate.RecordId, transaction);

    // LU2. Validate action
    await this.validateProjectUpdate(recordToUpdate, transaction);

    // LU3. Idempotent check
    if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
      return;
    }

    // LU4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<Project>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy<Project>(localCopy, existingRecord, checks);
    }

    // LU5. Validate model
    await this.validateProject(recordToUpdate, transaction);

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.Project, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getProjectByRecordId(recordToUpdate.RecordId, transaction);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // #region Low-level Validate Model

  async validateProject(project: Project, existingTransaction?: DbTransaction): Promise<void> {
    // TODO: Implement validation logic

    return;
  }

  async validateSoftDeletedProject(project: Project, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!this.checkIsProjectSoftDeleted(project)) {
      throw new StateValidationException('SoftDeleted', project);
    }

    return;
  }

  // #endregion
  // #region Low-level Validate Action
  async validateProjectCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  async validateProjectUpdate(existingRecord: Project, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }
  // #endregion
  // #region Mid-level Get One

  // #endregion
  // #region Mid-level Get Many

  async getActiveAssignedProjects(userId: number, expireComparisonDate: DateTimeType, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Project[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const projectIdsAssignedDirectly = await this.user2ProjectService.getProjectIdsByUserId(userId, transaction, includeDeleted);

    const assignedFieldActivities = await this.getActiveAssignedFieldActivitiesByUserId(userId, expireComparisonDate, transaction, includeDeleted);
    const projectIdsAssignedViaFieldActivities = this.fieldActivityService.getDistinctProjectIdsForFieldActivities(assignedFieldActivities);

    const assignedProjectIds = [...new Set([...projectIdsAssignedDirectly, ...projectIdsAssignedViaFieldActivities])];

    const projects = await this.getProjectsByOnlineIds(assignedProjectIds, transaction, includeDeleted);
    const activeProjects = this.filterForActiveProjects(projects, expireComparisonDate);

    return activeProjects;
  }

  async getActiveAssignedProjectIds(userId: number, expireComparisonDate: DateTimeType, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number[]> {
    const activeAssignedProjects = await this.getActiveAssignedProjects(userId, expireComparisonDate, existingTransaction, includeDeleted);
    return activeAssignedProjects.map(project => project.Id);
  }

  // TODO: move this to more appropriate place
  async getActiveAssignedFieldActivitiesByUserId(userId: number, expireComparisonDate: DateTimeType, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<FieldActivity[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const assignedFieldActivities = await this.getAssignedFieldActivitiesByUserId(userId, transaction, includeDeleted);

    let activeAssignedFieldActivities = await Promise.all(assignedFieldActivities.map(async (fieldActivity) => {
      try {
        const projectPromise = this.getProjectByOnlineId(fieldActivity.ProjectId, transaction, includeDeleted);
        const laborCategoryPromise = this.laborCategoryService.getLaborCategoryByOnlineId(fieldActivity.LaborCategoryId, transaction, includeDeleted);
        const taskPromise = this.taskService.getTaskByOnlineId(fieldActivity.TaskId, transaction, includeDeleted);

        const [project, laborCategory, task] = await Promise.all([projectPromise, laborCategoryPromise, taskPromise]);

        const isProjectActive = this.checkIsProjectActive(project, expireComparisonDate);
        const isLaborCategoryActive = this.laborCategoryService.checkIsLaborCategoryActive(laborCategory, expireComparisonDate);
        const isTaskActive = this.taskService.checkIsTaskActive(task, expireComparisonDate);

        return isProjectActive && isLaborCategoryActive && isTaskActive ? fieldActivity : null;
      }
      catch (e) {
        if (e instanceof RecordNotFoundException || e instanceof RecordSoftDeletedException) {
          return null;
        }
        throw e;
      }
    }));

    activeAssignedFieldActivities = activeAssignedFieldActivities.filter(activity => activity !== null);

    return activeAssignedFieldActivities;
  }

  // TODO: move this to more appropriate place
  async getActiveAssignedFieldActivitiesByUserIdProjectId(userId: number, projectId: number, expireComparisonDate: DateTimeType, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<FieldActivity[]> {
    const activeAssignedFieldActivities = await this.getActiveAssignedFieldActivitiesByUserId(userId, expireComparisonDate, existingTransaction, includeDeleted);
    return this.fieldActivityService.filterFieldActivitiesByProjectId(activeAssignedFieldActivities, projectId)
  }

  // TODO: move this to more appropriate place
  async getAssignedFieldActivitiesByUserId(userId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<FieldActivity[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const assignedFieldActivityIds = await this.user2FieldActivityService.getFieldActivityIdsByUserId(userId, transaction, includeDeleted);
    const assignedFieldActivities = await this.fieldActivityService.getFieldActivitiesByOnlineIds(assignedFieldActivityIds, transaction, includeDeleted);
    return assignedFieldActivities;
  }

  // TODO: move this to more appropriate place
  async getMatchingAssignedFieldActivitiesForProject(userId: number, projectId: number, taskId: number, laborCategoryId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<FieldActivity[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const assignedFieldActivities = await this.getAssignedFieldActivitiesByUserId(userId, transaction, includeDeleted);

    return assignedFieldActivities.filter(fieldActivity =>
      fieldActivity.ProjectId === projectId
      && fieldActivity.TaskId === taskId
      && fieldActivity.LaborCategoryId === laborCategoryId
    );
  }

  // TODO: move this to more appropriate place
  async guessFieldActivity(userId: number, projectId: number, taskId: number, laborCategoryId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<FieldActivity> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const matchingFieldActivities = await this.getMatchingAssignedFieldActivitiesForProject(userId, projectId, taskId, laborCategoryId, transaction, includeDeleted);

    if (matchingFieldActivities.length === 1) {
      return matchingFieldActivities[0];
    } else if (matchingFieldActivities.length > 1) {
      console.log('Warning: Multiple FieldActivities found matching criteria. The first matching FieldActivity was returned.');
      return matchingFieldActivities[0];
    } else {
      throw Error('No FieldActivity found matching criteria.');
    }
  }

  // #endregion
  // #region Mid-level Sync

  async upsertProjectFromSync(who: DataChangeEmitWho, recordToUpsert: Project, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getProjectByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;
      await this.updateProject(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createProject(who, recordToUpsert, transaction, true);
      }
    }

    return;
  }

  // #endregion
  // #region Check

  checkIsProjectActive(project: Project, expireComparisonDate: DateTimeType): boolean {
    const compareDate = DateTimeHelper.formatDateTimeAsDate(expireComparisonDate);
    return (!project.StartDate || DateTimeHelper.checkTimeIsAfter(compareDate, undefined, DateTimeHelper.formatDateTimeAsDate(project.StartDate), undefined, true))
      &&  (!project.EndDate || DateTimeHelper.checkTimeIsBefore(compareDate, undefined, DateTimeHelper.formatDateTimeAsDate(project.EndDate), undefined, true));
  }

  checkIsFieldActivityProject(project: Project): boolean {
    return project.EnableFieldActivities;
  }

  checkIsProjectSoftDeleted(project: Project): boolean {
    return project.IsDeleted;
  }

  checkIsUserProjectSoftDeleted(userProject: UserProject): boolean {
    return userProject.IsDeleted;
  }

  private checkImmutableFieldsMatch(a: Project, b: Project): boolean {
    // TODO: Implement checkImmutableFieldsMatch

    return true;
  }

  // #endregion
  // #region Lookup


  // TODO: move this to more appropriate place
  async getFieldActivityName(fieldActivityId?: number, userId?: number, projectId?: number, taskId?: number, laborCategoryId?: number, existingTransaction?: DbTransaction): Promise<string> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    let fieldActivity: FieldActivity;
    if (fieldActivityId) {
      fieldActivity = await this.fieldActivityService.getFieldActivityByOnlineId(fieldActivityId, transaction);
    } else if (userId && projectId && taskId && laborCategoryId) {
      fieldActivity = await this.guessFieldActivity(userId, projectId, taskId, laborCategoryId, transaction);
    } else {
      throw Error('Missing parameters to get FieldActivity.');
    }

    if (fieldActivity === null) {
      throw new RecordNotFoundException(fieldActivityId);
    }

    return fieldActivity.ActivityName;
  }


  // #endregion
  // #region Transform

  filterForUniqueProjects(projects: Project[]): Project[] {
    return projects.filter((project, index, self) => self.findIndex(p => p.Id === project.Id) === index);
  }

  filterForActiveProjects(projects: Project[], expireComparisonDate: DateTimeType): Project[] {
    return projects.filter(project => this.checkIsProjectActive(project, expireComparisonDate));
  }

  sortProjectsByName(projects: Project[]): Project[] {
    return projects.sort((a, b) => (a.Name ?? '').localeCompare(b.Name ?? ''));
  }

  // #endregion

}
